import { Link } from '@tanstack/react-router'
import type { NavBadgeValue } from '@workspace/seedforge-ui/components/app-sidebar'
import {
  FOUNDER_NAV_INVESTOR_CAMPAIGN_LABEL,
  FOUNDER_NAV_MATCHED_INVESTORS_LABEL,
  FOUNDER_NAV_SHARE_PROFILE_LABEL,
  FOUNDER_NAV_WARM_INTRO_CAMPAIGN_LABEL,
} from '@workspace/seedforge-ui/lib/founder-nav'
import { cn } from '@workspace/ui/lib/utils'
import { Link2, Send } from 'lucide-react'
import type { ReactNode } from 'react'
import { useBasePath } from '../../../hooks/use-base-path'
import { useWorkspaceReady } from '../../../hooks/use-workspace-ready'
import { useOutreachNavBadges } from '../../outreach/hooks/use-outreach-nav-badges'

export type ShareProfileSection = 'share-links' | 'outreach'
export type OutreachChannel = 'investors' | 'portfolio'

const SHARE_PROFILE_SUBTITLE = 'Send one link instead of your deck, and see how investors read it.'

const BADGE_TONE: Record<NavBadgeValue['tone'], string> = {
  primary: 'bg-primary',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
}

// SeedForge Outreach's two channels: one campaign page, with Warm-intro at #warm-intro as in the prototype.
const OUTREACH_SUB_TABS: { id: OutreachChannel; label: string; hash?: string }[] = [
  { id: 'investors', label: FOUNDER_NAV_INVESTOR_CAMPAIGN_LABEL },
  { id: 'portfolio', label: FOUNDER_NAV_WARM_INTRO_CAMPAIGN_LABEL, hash: 'warm-intro' },
]

// The Share your Profile page's card row (prototype v2--share.html): Matched investors as a button above the cards,
// then one card per place a founder shares, sends or reviews from. Outreach and Matched investors need matches, so
// they are disabled until the workspace is ready, as the old Outreach group was. SeedForge Discovery's card arrives with FA4.
export function ShareProfileTabs({
  startupId,
  active,
  channel,
}: {
  startupId: string
  active: ShareProfileSection
  /** The campaign on screen. Omit when no campaign is shown, which hides the sub tabs. */
  channel?: OutreachChannel
}) {
  const basePath = useBasePath()
  const { ready } = useWorkspaceReady()
  const badges = useOutreachNavBadges(startupId, { enabled: ready })
  const outreachBadge = badges[FOUNDER_NAV_SHARE_PROFILE_LABEL]

  const cards = [
    {
      id: 'share-links' as const,
      title: 'Share Links',
      description: 'Send links with their own settings',
      icon: Link2,
      to: '/profile/links',
      disabled: false,
      badge: undefined,
    },
    {
      id: 'outreach' as const,
      title: 'SeedForge Outreach',
      description: 'Investors and warm-intro messages sent through your LinkedIn',
      icon: Send,
      to: '/outreach',
      disabled: !ready,
      badge: outreachBadge,
    },
  ]

  return (
    <div className="mb-6">
      <nav
        aria-label="Share your Profile"
        className={cn(
          'grid grid-cols-2 gap-3',
          // Size the columns to the cards, so two cards fill the row rather than leaving a gap.
          cards.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3'
        )}
      >
        {cards.map((card) => {
          const Icon = card.icon
          const isActive = active === card.id
          const className = cn(
            'flex items-center gap-3 rounded-xl border p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            isActive
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground',
            card.disabled && 'pointer-events-none opacity-50'
          )
          const content = (
            <>
              <span
                className={cn(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted/40',
                  isActive ? 'text-primary' : 'text-foreground/70'
                )}
              >
                <Icon className="size-6" aria-hidden="true" />
              </span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="flex items-center text-sm font-semibold text-foreground">
                  {card.title}
                  {card.badge && (
                    <span
                      title={card.badge.title}
                      className={cn(
                        'ml-2 inline-block size-2 rounded-full',
                        BADGE_TONE[card.badge.tone]
                      )}
                    />
                  )}
                </span>
                <span className="text-xs leading-snug text-muted-foreground">
                  {card.description}
                </span>
              </span>
            </>
          )
          return card.disabled ? (
            <span key={card.id} aria-disabled="true" className={className}>
              {content}
            </span>
          ) : (
            <Link
              key={card.id}
              to={`${basePath}${card.to}` as string}
              aria-current={isActive ? 'page' : undefined}
              className={className}
            >
              {content}
            </Link>
          )
        })}
      </nav>

      {active === 'outreach' && ready && channel !== undefined && (
        <nav
          aria-label="SeedForge Outreach"
          className="mt-6 flex gap-1 overflow-x-auto border-b border-border"
        >
          {OUTREACH_SUB_TABS.map((sub) => {
            const badge = badges[sub.label]
            return (
              <Link
                key={sub.id}
                to={`${basePath}/outreach` as string}
                hash={sub.hash}
                aria-current={channel === sub.id ? 'page' : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                  channel === sub.id
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-foreground hover:text-foreground/80'
                )}
              >
                {sub.label}
                {badge && (
                  <span
                    title={badge.title}
                    className={cn(
                      'inline-flex items-center justify-center rounded-full text-[10px] font-semibold text-black',
                      BADGE_TONE[badge.tone],
                      badge.text ? 'h-5 min-w-5 px-1.5' : 'size-2'
                    )}
                  >
                    {badge.text}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>
      )}
    </div>
  )
}

/** The Matched investors button. Sits on the title line of the page it opens from; disabled until the workspace is ready. */
function MatchedInvestorsButton() {
  const { ready } = useWorkspaceReady()
  const basePath = useBasePath()
  const className =
    'inline-flex h-9 shrink-0 items-center rounded-lg border border-border bg-card px-3.5 text-sm font-semibold text-foreground hover:border-foreground/30'
  if (!ready) {
    return (
      <span aria-disabled="true" className={cn(className, 'pointer-events-none opacity-50')}>
        {FOUNDER_NAV_MATCHED_INVESTORS_LABEL}
      </span>
    )
  }
  return (
    <Link to={`${basePath}/investors` as string} className={className}>
      {FOUNDER_NAV_MATCHED_INVESTORS_LABEL}
    </Link>
  )
}

/** The Share your Profile title, subtitle and Matched investors button. Shared by every page in the section. */
export function ShareProfileHeader() {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {FOUNDER_NAV_SHARE_PROFILE_LABEL}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{SHARE_PROFILE_SUBTITLE}</p>
      </div>
      <MatchedInvestorsButton />
    </div>
  )
}

/**
 * The SeedForge Outreach page frame: header and card row above whatever the page
 * shows. Every state uses it, so the frame does not change between loading,
 * coming soon, disconnecting and the dashboard.
 */
export function ShareProfileOutreachFrame({
  startupId,
  channel,
  children,
}: {
  startupId: string
  channel?: OutreachChannel
  children: ReactNode
}) {
  return (
    <div className="mx-auto max-w-6xl py-2">
      <div className="mb-6">
        <ShareProfileHeader />
      </div>
      <ShareProfileTabs startupId={startupId} active="outreach" channel={channel} />
      {children}
    </div>
  )
}
